/**
 * AddButton - Standardized "Add" action button with loading state.
 *
 * A secondary button with Plus icon, responsive text, and loading spinner.
 * Used consistently for adding items across the application.
 *
 * @example
 * ```tsx
 * <AddButton
 *   label="Feature"
 *   onClick={handleAddFeature}
 *   isLoading={isAdding}
 * />
 * ```
 */

import { Plus, Loader2 } from 'lucide-react';
import { Button, type ButtonProps } from '@lean-agility/design-system/react';

export interface AddButtonProps {
  /** Label for the item being added (e.g., "Feature", "Team", "Deliverable") */
  label: string;
  /** Click handler */
  onClick: () => void;
  /** Whether the add operation is in progress */
  isLoading?: boolean;
  /** Loading state label */
  loadingLabel?: string;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Whether to always show full label on mobile (default: false, shows only label without "Add") */
  showFullLabelOnMobile?: boolean;
  /** Button size */
  size?: ButtonProps['size'];
}

export function AddButton({
  label,
  onClick,
  isLoading = false,
  loadingLabel = 'Adding...',
  disabled = false,
  showFullLabelOnMobile = false,
  size = 'sm',
}: AddButtonProps) {
  return (
    <Button type="button" variant="secondary" size={size} onClick={onClick} disabled={disabled || isLoading}>
      {isLoading ? (
        <>
          <Loader2 className="mr-1 size-4 animate-spin" />
          {loadingLabel}
        </>
      ) : (
        <>
          <Plus className="mr-1 size-4" />
          {showFullLabelOnMobile ? (
            <>Add {label}</>
          ) : (
            <>
              <span
                className="
                  hidden
                  sm:inline
                "
              >
                Add{' '}
              </span>
              {label}
            </>
          )}
        </>
      )}
    </Button>
  );
}
